import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Strikethrough, Code,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote,
  Undo2, Redo2, Link as LinkIcon, Minus, Eraser,
} from "lucide-react";
import { Button } from "./ui/button";

/**
 * RichTextEditor
 *
 * Headless TipTap WYSIWYG with a compact toolbar. Emits HTML via `onChange`.
 * Accepts either HTML or plain text as initial `value` — plain text is preserved
 * (TipTap wraps it in a <p> on first load) so existing CMS pages migrate cleanly.
 */
export function RichTextEditor({ value, onChange, placeholder, minHeight = 260, testid = "rte" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener nofollow", target: "_blank", class: "text-blue-600 underline underline-offset-2" },
        validate: (href) => /^https?:\/\//i.test(href) || href.startsWith("/") || href.startsWith("mailto:"),
      }),
      Placeholder.configure({ placeholder: placeholder || "Start writing…" }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-zinc max-w-none focus:outline-none px-4 py-3 min-h-[240px] " +
          "prose-headings:font-display prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline",
        "data-testid": `${testid}-surface`,
        style: `min-height:${minHeight}px`,
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      // Emit empty string when the editor is truly empty so the backend gets ""
      // instead of the placeholder <p></p>.
      onChange?.(e.isEmpty ? "" : html);
    },
  });

  // Keep external `value` in sync (e.g., loading a different page)
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL (https://…, mailto:… or /relative-path)", prev || "");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const btn = (isActive, onClick, Icon, label, tid) => (
    <Button
      type="button"
      size="sm"
      variant={isActive ? "default" : "outline"}
      onClick={onClick}
      className="h-8 w-8 p-0"
      aria-label={label}
      title={label}
      data-testid={`${testid}-${tid}`}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );

  return (
    <div className="rounded-md border border-zinc-200 overflow-hidden bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-zinc-200 bg-zinc-50">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Bold", "bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italic", "italic")}
        {btn(editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run(), Strikethrough, "Strikethrough", "strike")}
        {btn(editor.isActive("code"), () => editor.chain().focus().toggleCode().run(), Code, "Inline code", "code")}
        <div className="mx-1 h-5 w-px bg-zinc-200" />
        {btn(editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), Heading1, "Heading 1", "h1")}
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "Heading 2", "h2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, "Heading 3", "h3")}
        <div className="mx-1 h-5 w-px bg-zinc-200" />
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Bullet list", "ul")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Numbered list", "ol")}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), Quote, "Quote", "quote")}
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), Minus, "Horizontal rule", "hr")}
        <div className="mx-1 h-5 w-px bg-zinc-200" />
        {btn(editor.isActive("link"), setLink, LinkIcon, "Link", "link")}
        {btn(false, () => editor.chain().focus().unsetAllMarks().clearNodes().run(), Eraser, "Clear formatting", "clear")}
        <div className="mx-1 h-5 w-px bg-zinc-200" />
        {btn(false, () => editor.chain().focus().undo().run(), Undo2, "Undo", "undo")}
        {btn(false, () => editor.chain().focus().redo().run(), Redo2, "Redo", "redo")}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
